import { useMemo } from 'react';
import { View, Text, Pressable, ScrollView, Image } from 'react-native';
import { reportError } from '@/lib/errors';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useRouter, usePathname, type Href } from 'expo-router';
import Animated, { FadeInLeft } from 'react-native-reanimated';
import { useThemeStore } from '@/lib/state/theme-store';
import {
  Home,
  Inbox,
  BarChart3,
  FolderOpen,
  MessageCircle,
  Sparkles,
  Building2,
  LayoutGrid,
  FileSearch,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
  User,
  Briefcase,
  LayoutDashboard,
  MessageSquare,
  Users2,
  Activity,
  Flag,
  DollarSign,
  Timer,
  UserX,
  Gauge,
} from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { webDesign } from '@/lib/platform';
import { useInverioStore } from '@/lib/state/inverio-store';
import { useProfileStore } from '@/lib/state/profile-store';
import { useIsAdmin } from '@/lib/auth/role';
import { useSubscriptionStore } from '@/lib/state/subscription-store';
import { useAuth } from '@/lib/supabase';
import { useFlag } from '@/lib/feature-flags';
import { useUnreadMessageCount } from '@/lib/messaging/use-unread-messages';

interface NavItem {
  label: string;
  icon: typeof BarChart3;
  path: string;
  badge?: number;
}

// Startup navigation items
const STARTUP_NAV: NavItem[] = [
  { label: 'Home', icon: Home, path: '/(startup)/startup-dashboard' },
  { label: 'Score', icon: BarChart3, path: '/(startup)/score' },
  { label: 'Data Room', icon: FolderOpen, path: '/(startup)/dataroom' },
  { label: 'Messages', icon: MessageCircle, path: '/(startup)/startup-messages' },
  // NAMED FOR WHAT IT OPENS. This said "AI Insights" while pointing at
  // /recommendations, and the separate conversational product at /insights —
  // the actual advisor — had no sidebar entry at all, so one label covered two
  // products and neither name reached the right screen. The advisor is
  // deliberately still NOT listed: `ai_coach` is seeded off, and a menu must
  // not advertise a paused AI product.
  { label: 'Recommendations', icon: Sparkles, path: '/(startup)/recommendations' },
  { label: 'Company Profile', icon: Building2, path: '/(startup)/company' },
];

// Investor navigation items (Profile accessed via user card at bottom)
//
// ORDER FOLLOWS THE EARLY-STAGE INVESTMENT LIFECYCLE, deliberately — not
// alphabetical and not by frequency of use:
//
//   Home → Submissions → Dealflow → Due Diligence → Messages → Portfolio
//
// A deal arrives (Submissions), enters the pipeline (Dealflow), gets
// investigated (Due Diligence), is negotiated (Messages), and — if it closes —
// becomes a holding (Portfolio). Anyone tempted to "tidy" this into a different
// order should know it encodes the workflow.
//
// `Submissions` was missing entirely despite the route existing, so the inbox
// where deals actually arrive had no sidebar entry on web.
const INVESTOR_NAV: NavItem[] = [
  { label: 'Home', icon: Home, path: '/(investor)/investor-dashboard' },
  { label: 'Submissions', icon: Inbox, path: '/(investor)/submissions' },
  { label: 'Dealflow', icon: LayoutGrid, path: '/(investor)/dealflow' },
  { label: 'Due Diligence', icon: FileSearch, path: '/(investor)/duediligence' },
  { label: 'Messages', icon: MessageCircle, path: '/(investor)/investor-messages' },
  { label: 'Portfolio', icon: Briefcase, path: '/(investor)/portfolio' },
  // AI Assistant is appended at render time, gated on the `ai_coach` flag —
  // see AI_ASSISTANT_NAV below.
];

// Paused for beta/launch — hidden until further notice, like the startup-side
// coach, but on its OWN flag (`ai_assistant`).
//
// The investor assistant and the startup coach are different products: this one
// is deal analysis backed by `useDDChat`, the startup one is founder coaching
// backed by `startup-insights`. They were briefly gated on the single
// `ai_coach` key, which would have meant un-pausing founder coaching also
// exposed the investor assistant. Separate products, separate switches.
//
// Default OFF; re-enable from the admin flags screen with no redeploy. The FAB
// on the investor screens was already gated, but this sidebar entry was not —
// so the assistant stayed reachable on web while hidden everywhere else.
const AI_ASSISTANT_NAV: NavItem = {
  label: 'AI Assistant',
  icon: Sparkles,
  path: '/(investor)/assistant',
};

// Admin navigation items
const ADMIN_NAV: NavItem[] = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/(admin)/admin-dashboard' },
  { label: 'Analytics', icon: BarChart3, path: '/(admin)/executive-dashboard' },
  { label: 'Product', icon: Activity, path: '/(admin)/product-analytics' },
  { label: 'Feedback', icon: MessageSquare, path: '/(admin)/feedback' },
  { label: 'Users', icon: Users2, path: '/(admin)/users' },
  { label: 'Flags', icon: Flag, path: '/(admin)/feature-flags' },
  { label: 'Retention', icon: Timer, path: '/(admin)/data-retention' },
  { label: 'Erasure', icon: UserX, path: '/(admin)/erasure-requests' },
  { label: 'AI Cost', icon: DollarSign, path: '/(admin)/ai-cost' },
  { label: 'AI Quality', icon: Gauge, path: '/(admin)/ai-quality' },
];

interface WebSidebarProps {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

import { LOGO_MARK } from '@/lib/logo';

export function WebSidebar({ collapsed = false, onToggleCollapse }: WebSidebarProps) {
  const router = useRouter();
  const pathname = usePathname();
  const colors = useThemeColors();
  const isDark = useThemeStore((s) => s.isDark);
  const personaRole = useProfileStore((s) => s.userRole);
  const startupProfile = useProfileStore((s) => s.startupProfile);
  const investorProfile = useProfileStore((s) => s.investorProfile);
  const resetInverioStore = useInverioStore((s) => s.reset);
  const resetSubscriptionStore = useSubscriptionStore((s) => s.reset);
  const { user: authUser, signOut } = useAuth();

  // ADMINISTRATIVE AUTHORITY, asked of the server — the same hook the /(admin)
  // guard and the router use.
  //
  // This read the persisted store, which is a CACHE maintained by index.tsx's
  // effect. Land on /(admin)/admin-dashboard directly — bookmark, refresh, deep
  // link — and index never mounts, so the cache still holds the PERSONA
  // ('investor', which is correct: admin is not a persona). The result was the
  // admin shell showing a "Super Admin" badge and live admin data while this
  // sidebar rendered investor navigation and an "Investor" label around it.
  //
  // Authority is not something the chrome may cache a second copy of.
  const { isAdmin } = useIsAdmin(authUser?.id);
  const userRole = isAdmin ? 'admin' : personaRole;

  // Investor-assistant kill-switch — NOT the startup coach's. Hook runs
  // unconditionally, before any early return, per the rules of hooks.
  const aiAssistantEnabled = useFlag('ai_assistant', { default: false });

  // The tab bar is display:none on web — navigation is this sidebar — so
  // without this the unread badge would simply not exist for web users. An
  // admin has no messages inbox of their own; the hook resolves no profile id
  // for them and returns 0.
  const unreadMessages = useUnreadMessageCount(userRole === 'startup' ? 'startup' : 'investor');

  const navItems = useMemo(() => {
    // The NAV consts are module-level and shared across renders, so the count
    // is merged in here rather than mutated into them.
    const withUnread = (items: NavItem[]) =>
      unreadMessages > 0
        ? items.map((item) =>
            item.path.includes('messages') ? { ...item, badge: unreadMessages } : item
          )
        : items;

    if (userRole === 'admin') return ADMIN_NAV;
    if (userRole === 'startup') return withUnread(STARTUP_NAV);
    return withUnread(aiAssistantEnabled ? [...INVESTOR_NAV, AI_ASSISTANT_NAV] : INVESTOR_NAV);
  }, [userRole, aiAssistantEnabled, unreadMessages]);
  const user = useProfileStore((s) => s.user);

  // For the user card at bottom: show user's personal name (or fallback to profile name)
  // User's personal name - for investors, this comes from user object, not investorProfile
  const userName = user?.name || (userRole === 'admin' ? 'Admin' : userRole === 'startup' ? 'Team Member' : 'Investor');
  const userEmail = user?.email;
  const userRoleLabel = userRole === 'admin'
    ? 'Administrator'
    : userRole === 'startup'
    ? (user?.role || 'Founder')
    : 'Investor';

  const width = collapsed ? webDesign.sidebarCollapsedWidth : webDesign.sidebarWidth;

  const isActive = (path: string) => pathname.startsWith(path.replace('/(startup)', '').replace('/(investor)', '').replace('/(admin)', ''));

  const handleLogout = async () => {
    try {
      // Sign out from Supabase (clears localStorage)
      await signOut();
      // Clear local stores
      resetInverioStore();
      resetSubscriptionStore();
      // Force hard reload to clear all state
      if (typeof window !== 'undefined') {
        window.location.replace('/login');
      } else {
        router.replace('/login');
      }
    } catch (error) {
      reportError(error, { context: 'WebSidebar.handleLogout' });
      // Force reload anyway
      if (typeof window !== 'undefined') {
        window.location.replace('/login');
      }
    }
  };

  return (
    <Animated.View
      entering={FadeInLeft.duration(300)}
      style={{
        width,
        backgroundColor: colors.surface.primary,
        borderRightWidth: 1,
        borderRightColor: colors.border.light,
        height: '100%',
      }}
    >
      <View className="flex-1">
        {/* Logo / Brand */}
        <View
          className="px-4 py-5 flex-row items-center gap-3"
          style={{
            borderBottomWidth: 1,
            borderBottomColor: colors.border.light,
            height: webDesign.headerHeight,
          }}
        >
          {/* App Icon - always visible */}
          <Image
            source={LOGO_MARK}
            style={{ width: 40, height: 40, borderRadius: 10 }}
            resizeMode="contain"
          />
          {/* Company Name - hidden when collapsed */}
          {!collapsed && (
            <Text style={{ fontSize: 22, fontWeight: '800', color: colors.text.primary, letterSpacing: -0.5 }}>
              inverio
            </Text>
          )}
        </View>

        {/* Navigation Items */}
        <ScrollView className="flex-1 py-4" showsVerticalScrollIndicator={false}>
          <View className="px-3 gap-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <AnimatedPressable
                  key={item.path}
                  onPress={() => router.push(item.path as Href)}
                  className="flex-row items-center gap-3 rounded-xl"
                  style={{
                    paddingVertical: 12,
                    paddingHorizontal: collapsed ? 12 : 16,
                    backgroundColor: active ? colors.primary[50] : 'transparent',
                    justifyContent: collapsed ? 'center' : 'flex-start',
                  }}
                >
                  <Icon
                    size={22}
                    color={active ? colors.primary[600] : colors.text.secondary}
                  />
                  {!collapsed && (
                    <Text
                      style={{
                        color: active ? colors.primary[600] : colors.text.secondary,
                        fontWeight: active ? '600' : '500',
                      }}
                      className="text-sm flex-1"
                    >
                      {item.label}
                    </Text>
                  )}
                  {!collapsed && item.badge && (
                    <View
                      className="px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: colors.traffic.red }}
                    >
                      <Text className="text-xs font-semibold text-white">
                        {item.badge}
                      </Text>
                    </View>
                  )}
                </AnimatedPressable>
              );
            })}
          </View>

          {/* Divider */}
          <View
            className="mx-4 my-4"
            style={{ height: 1, backgroundColor: colors.border.light }}
          />

          {/* Secondary Nav */}
          <View className="px-3 gap-1">
            <AnimatedPressable
              onPress={() => router.push('/settings')}
              className="flex-row items-center gap-3 rounded-xl"
              style={{
                paddingVertical: 12,
                paddingHorizontal: collapsed ? 12 : 16,
                justifyContent: collapsed ? 'center' : 'flex-start',
              }}
            >
              <Settings size={20} color={colors.text.tertiary} />
              {!collapsed && (
                <Text
                  style={{ color: colors.text.tertiary }}
                  className="text-sm"
                >
                  Settings
                </Text>
              )}
            </AnimatedPressable>
            <AnimatedPressable
              onPress={() => router.push(userRole === 'startup' ? '/(startup)/help' : '/(investor)/help')}
              className="flex-row items-center gap-3 rounded-xl"
              style={{
                paddingVertical: 12,
                paddingHorizontal: collapsed ? 12 : 16,
                justifyContent: collapsed ? 'center' : 'flex-start',
              }}
            >
              <HelpCircle size={20} color={colors.text.tertiary} />
              {!collapsed && (
                <Text
                  style={{ color: colors.text.tertiary }}
                  className="text-sm"
                >
                  Help & Support
                </Text>
              )}
            </AnimatedPressable>
          </View>
        </ScrollView>

        {/* User Profile / Logout */}
        <View
          className="px-3 py-4"
          style={{
            borderTopWidth: 1,
            borderTopColor: colors.border.light,
          }}
        >
          {/* User Profile Card - links to role-specific user profile page */}
          {userName && (
            <AnimatedPressable
              onPress={() => router.push(userRole === 'admin' ? '/settings' : userRole === 'investor' ? '/(investor)/investorprofile' : '/(startup)/userprofile')}
              className="flex-row items-center gap-3 mb-3 rounded-xl"
              style={{
                paddingVertical: collapsed ? 8 : 10,
                paddingHorizontal: collapsed ? 8 : 12,
                backgroundColor: colors.surface.secondary,
                justifyContent: collapsed ? 'center' : 'flex-start',
              }}
            >
              <View
                className="w-9 h-9 rounded-full items-center justify-center"
                style={{ backgroundColor: colors.primary[100] }}
              >
                <User size={18} color={colors.primary[600]} />
              </View>
              {!collapsed && (
                <View className="flex-1">
                  <Text
                    style={{ color: colors.text.primary }}
                    className="font-medium text-sm"
                    numberOfLines={1}
                  >
                    {userName}
                  </Text>
                  <Text
                    style={{ color: colors.text.tertiary }}
                    className="text-xs capitalize"
                  >
                    {userRoleLabel}
                  </Text>
                </View>
              )}
            </AnimatedPressable>
          )}

          <AnimatedPressable
            onPress={handleLogout}
            className="flex-row items-center gap-3 rounded-xl"
            style={{
              paddingVertical: 12,
              paddingHorizontal: collapsed ? 12 : 16,
              backgroundColor: colors.red.light,
              justifyContent: collapsed ? 'center' : 'flex-start',
            }}
          >
            <LogOut size={20} color={colors.traffic.red} />
            {!collapsed && (
              <Text
                style={{ color: colors.traffic.red }}
                className="text-sm font-medium"
              >
                Sign Out
              </Text>
            )}
          </AnimatedPressable>
        </View>

        {/* Collapse Toggle */}
        {onToggleCollapse && (
          <Pressable
            onPress={onToggleCollapse}
            className="absolute right-0 top-1/2 w-6 h-12 items-center justify-center rounded-l-lg"
            style={{
              backgroundColor: colors.surface.secondary,
              transform: [{ translateY: -24 }, { translateX: 12 }],
            }}
          >
            {collapsed ? (
              <ChevronRight size={16} color={colors.text.tertiary} />
            ) : (
              <ChevronLeft size={16} color={colors.text.tertiary} />
            )}
          </Pressable>
        )}
      </View>
    </Animated.View>
  );
}
